import { useMemo, useState } from "react";
import styles from "../styles/shared/ReportBoard.module.css";
import { Button, message } from "antd";
import { FaFileAlt, FaFileCsv, FaRegCopy, FaRegFileCode, FaRegFilePdf } from "react-icons/fa";
import { FiChevronDown, FiChevronUp, FiRefreshCcw } from "react-icons/fi";
import AssistantVisualBoard, { boardCoinMetrics } from "../components/trading-assistant/assistant/AssistantVisualBoard";
import {
  boardClipboardText,
  boardDisplay,
  downloadTextFile,
  snapshotCsv,
  snapshotHtml,
  snapshotJson,
} from "./boardSnapshot";
import { normalizeLiveTimeframe } from "../redux/live-suggestion/liveSuggestionSlice";

const EXPORT_ITEMS = [
  { key: "pdf_report", label: "PDF", icon: <FaRegFilePdf />, local: false },
  { key: "html_report", label: "HTML", icon: <FaRegFileCode />, local: true, ext: "html" },
  { key: "csv_report", label: "CSV", icon: <FaFileCsv />, local: true, ext: "csv" },
  { key: "json_report", label: "JSON", icon: <FaFileAlt />, local: true, ext: "json" },
];

function SideMark({ side, count }) {
  const kind = side === "Buy" ? styles.buyChip : side === "Sell" ? styles.sellChip : styles.holdChip;
  return (
    <span className={`${styles.countChip} ${kind}`}>
      {count != null ? `${count} ` : ""}
      {side}
    </span>
  );
}

function biasKind(bias) {
  const text = String(bias || "").toLowerCase();
  if (text.includes("short")) return "sell";
  if (text.includes("long")) return "buy";
  return "hold";
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

const STYLE_GUIDE =
  "https://cryptocrispy.com.au/blog/2026-10-05-swing-scalping-and-hold.html";

const STYLE_LINKS = [
  ["swing trading", `${STYLE_GUIDE}#swing-trading`],
  ["buy and hold", `${STYLE_GUIDE}#buy-and-hold`],
  ["range trading", `${STYLE_GUIDE}#range-trading`],
  ["scalping", `${STYLE_GUIDE}#scalping`],
];

function renderGuideLine(line) {
  const text = String(line || "");
  const lower = text.toLowerCase();
  const match = STYLE_LINKS.find(([phrase]) => lower.includes(phrase));
  if (!match) return text;
  const [phrase, href] = match;
  const index = lower.indexOf(phrase);
  return (
    <>
      {text.slice(0, index)}
      <a href={href} target="_blank" rel="noopener noreferrer">
        {text.slice(index, index + phrase.length)}
      </a>
      {text.slice(index + phrase.length)}
    </>
  );
}

function suggestedApproach(board, live, tf) {
  if ((live === "Buy" || live === "Sell") && board && live !== board) {
    return board === "Buy" ? "buy and hold" : "keep the existing side";
  }
  if (live !== "Buy" && live !== "Sell") {
    return board === "Buy" ? "buy and hold" : "range trading";
  }
  if (tf === "15m") return "scalping";
  if (tf === "1h" || tf === "4h") return "swing trading";
  if (board === "Buy") return "buy and hold";
  return "keep the existing side";
}

function traderRead(actions, metrics, timeframe) {
  const tf = normalizeLiveTimeframe(timeframe);
  const buy = Number(actions?.Buy) || 0;
  const sell = Number(actions?.Sell) || 0;
  const board = buy > sell ? "Buy" : sell > buy ? "Sell" : "";
  const live =
    metrics.liveBuy > metrics.liveSell
      ? "Buy"
      : metrics.liveSell > metrics.liveBuy
        ? "Sell"
        : "Hold";
  const approach = `The fit for this board is ${suggestedApproach(board, live, tf)}.`;
  const portfolio =
    board === "Buy"
      ? "Keep the Buy positions already on the book."
      : board === "Sell"
        ? "Keep the Sell positions already on the book."
        : "Leave current positions as they are.";
  if (live === "Hold") {
    return [approach, portfolio, `The ${tf} side is Hold, so there is no short-term trade.`];
  }
  if (board && live === board) {
    return [approach, portfolio, `The ${tf} side agrees with the book, so a short-term trade can follow it.`];
  }
  return [approach, portfolio, `The ${tf} side disagrees with the book. Do not take a short-term trade against it.`];
}

const CONVERT_PAIRS = [
  ["gold", "btc", "Convert gold to BTC."],
  ["btc", "gold", "Convert BTC to gold."],
  ["gold", "silver", "Convert gold to silver."],
  ["silver", "gold", "Convert silver to gold."],
  ["gold", "usd", "Convert gold to USD."],
  ["usd", "gold", "Convert USD to gold."],
  ["silver", "usd", "Convert silver to USD."],
  ["usd", "silver", "Convert USD to silver."],
  ["btc", "usd", "Convert BTC to USD."],
  ["usd", "btc", "Convert USD to BTC."],
];

function convertAsset(symbol) {
  const base = String(symbol || "").split("/")[0].toUpperCase().replace("-", "");
  if (base === "XAUUSD" || base === "XAU" || base === "PAXG" || base === "PAXGUSDT") return "gold";
  if (base === "XAGUSD" || base === "XAG") return "silver";
  if (base === "BTC" || base === "BTCUSDT" || base === "XBT") return "btc";
  return "";
}

function conversionFromBoard(visual) {
  const votes = {};
  let seen = false;
  [...(visual?.symbols || []), ...(visual?.rows || [])].forEach((row) => {
    const asset = convertAsset(row?.symbol || row?.coin);
    if (!asset) return;
    seen = true;
    const side = String(row?.action || row?.suggestion || "").split("/")[0];
    if (side !== "Buy" && side !== "Sell") return;
    votes[asset] = votes[asset] || { Buy: 0, Sell: 0 };
    votes[asset][side] += 1;
  });
  if (!seen) return "";
  const sides = {};
  Object.entries(votes).forEach(([asset, tally]) => {
    if (tally.Buy > tally.Sell) sides[asset] = "Buy";
    else if (tally.Sell > tally.Buy) sides[asset] = "Sell";
  });
  const ok = (src, dst) => {
    if (dst === "usd") return sides[src] === "Sell";
    if (src === "usd") return sides[dst] === "Buy";
    return sides[src] === "Sell" && sides[dst] === "Buy";
  };
  const found = CONVERT_PAIRS.find(([src, dst]) => ok(src, dst));
  return found ? found[2] : "No conversion between gold, silver, and bitcoin.";
}

function formatStamp(value) {
  const text = String(value || "").trim();
  const match = text.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return text;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[Number(match[2]) - 1];
  if (!month) return text;
  return `${Number(match[3])} ${month}, ${match[4]}:${match[5]}`;
}

function BoardBrief({
  display,
  biasTone,
  actions,
  metrics,
  hasVisual,
  visualSummary,
  lines,
  generatedAt,
  nextRefreshAt,
}) {
  const bookTotal =
    (Number(actions?.Buy) || 0) + (Number(actions?.Sell) || 0) + (Number(actions?.Hold) || 0);
  if (!display.bias && !lines.length && !generatedAt && !nextRefreshAt && !hasVisual && !bookTotal) {
    return null;
  }
  const toneClass =
    biasTone === "buy" ? styles.biasBuy : biasTone === "sell" ? styles.biasSell : styles.biasHold;
  return (
    <div className={styles.brief}>
      {(generatedAt || nextRefreshAt) && (
        <p className={styles.meta}>
          {generatedAt ? `Updated ${formatStamp(generatedAt)}` : ""}
          {nextRefreshAt ? `${generatedAt ? " · " : ""}Refreshes ${formatStamp(nextRefreshAt)}` : ""}
        </p>
      )}
      {display.bias ? <p className={`${styles.biasPill} ${toneClass}`}>{display.bias}</p> : null}
      {bookTotal > 0 && (
        <div className={styles.mixLine}>
          <SideMark side="Buy" count={Number(actions.Buy) || 0} />
          <SideMark side="Sell" count={Number(actions.Sell) || 0} />
          <SideMark side="Hold" count={Number(actions.Hold) || 0} />
        </div>
      )}
      {hasVisual && (
        <div className={styles.mixLine}>
          <span className={styles.frameChip}>{normalizeLiveTimeframe(visualSummary?.live_timeframe)}</span>
          <SideMark side="Buy" count={metrics.liveBuy} />
          <SideMark side="Sell" count={metrics.liveSell} />
          <span className={styles.pivot}>
            {metrics.pivots} pivot {metrics.pivots === 1 ? "turn" : "turns"}
          </span>
          <span className={metrics.conflicts ? styles.conflict : styles.hold}>
            {metrics.conflicts} {metrics.conflicts === 1 ? "disagreement" : "disagreements"}
          </span>
        </div>
      )}
      {lines.length > 0 && (
        <ul className={styles.notes}>
          {lines.map((line, index) => (
            <li key={`${index}-${line}`}>{renderGuideLine(line)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportBoard({
  title = "Board summary",
  variant = "assistant",
  summary,
  exportLinks,
  visualSummary,
  freshness,
  onRequest,
  loading = false,
  error = false,
  disabled = false,
  actionLabel,
  outdated = false,
  statusNote = "",
  statusError = false,
  hideSummary = false,
  leading = null,
  more = null,
}) {
  const summaryText = summary?.text?.trim();
  const sections = summary?.sections || {};
  const [copied, setCopied] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const publicFreshness = useMemo(
    () =>
      freshness
        ? {
            generated_at: freshness.generated_at,
            data_as_of: freshness.data_as_of,
            next_refresh_at: freshness.next_refresh_at,
          }
        : freshness,
    [freshness],
  );
  const snapshot = useMemo(
    () =>
      boardClipboardText({
        variant,
        summaryText,
        sections,
        visualSummary,
        freshness: publicFreshness,
      }),
    [variant, summaryText, sections, visualSummary, publicFreshness],
  );
  const display = useMemo(() => boardDisplay(snapshot), [snapshot]);
  const metrics = useMemo(() => boardCoinMetrics(visualSummary), [visualSummary]);
  const actions = visualSummary?.actions || {};
  const biasTone = biasKind(display.bias);
  const hasVisual = Boolean(
    visualSummary?.rows?.length ||
      visualSummary?.symbols?.length ||
      visualSummary?.pivots?.length ||
      Object.values(visualSummary?.actions || {}).some((value) => Number(value) > 0) ||
      visualSummary?.macro_regime?.summary ||
      Object.keys(visualSummary?.categories || {}).length,
  );
  const canCopy = Boolean(summaryText || hasVisual);
  const hasReport = Boolean(summaryText || exportLinks?.pdf_report || canCopy || hasVisual);

  if (!hasReport && !onRequest && !statusNote) return null;

  const resolvedActionLabel =
    actionLabel ||
    (loading
      ? "Loading…"
      : hasReport
        ? "Refresh"
        : error
          ? "Try again"
          : "Load summary & reports");

  const generatedAt = publicFreshness?.generated_at || publicFreshness?.data_as_of;
  const nextRefreshAt = publicFreshness?.next_refresh_at;
  const serverLines = Array.isArray(summary?.trader_lines)
    ? summary.trader_lines.map((line) => String(line || "").trim()).filter(Boolean)
    : [];
  const localConvert = serverLines.length < 4 && hasVisual ? conversionFromBoard(visualSummary) : "";
  const briefLines = (
    serverLines.length >= 3
      ? serverLines
      : hasVisual
        ? traderRead(actions, metrics, visualSummary?.live_timeframe)
        : []
  ).concat(localConvert ? [localConvert] : []);
  const brief = (
    <BoardBrief
      display={display}
      biasTone={biasTone}
      actions={actions}
      metrics={metrics}
      hasVisual={hasVisual}
      visualSummary={visualSummary}
      lines={briefLines}
      generatedAt={generatedAt}
      nextRefreshAt={nextRefreshAt}
    />
  );

  const copyBoard = async () => {
    if (!snapshot) return;
    try {
      await writeClipboard(snapshot);
      setCopied(true);
      message.success("Board summary copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      message.error("Couldn't copy the board summary.");
    }
  };

  const downloadLocal = (item) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const base = variant === "insight" ? "insight-board" : "assistant-board";
    if (item.ext === "csv") {
      downloadTextFile(`${base}-${stamp}.csv`, "text/csv;charset=utf-8", snapshotCsv(variant, visualSummary));
      return;
    }
    if (item.ext === "html") {
      downloadTextFile(
        `${base}-${stamp}.html`,
        "text/html;charset=utf-8",
        snapshotHtml(title, snapshot, variant, visualSummary),
      );
      return;
    }
    downloadTextFile(
      `${base}-${stamp}.json`,
      "application/json",
      snapshotJson({ summary, visualSummary, freshness: publicFreshness, text: snapshot }),
    );
  };

  const bookTotal =
    (Number(actions?.Buy) || 0) + (Number(actions?.Sell) || 0) + (Number(actions?.Hold) || 0);

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.closedRow}>
          {display.bias ? <p className={`${styles.biasPill} ${biasTone === "buy" ? styles.biasBuy : biasTone === "sell" ? styles.biasSell : styles.biasHold}`}>{display.bias}</p> : null}
          {bookTotal > 0 && (
            <div className={styles.mixLine}>
              <SideMark side="Buy" count={Number(actions.Buy) || 0} />
              <SideMark side="Sell" count={Number(actions.Sell) || 0} />
              <SideMark side="Hold" count={Number(actions.Hold) || 0} />
            </div>
          )}
          {visualSummary?.macro_regime?.summary ? (
            <p className={styles.text}>{visualSummary.macro_regime.summary}</p>
          ) : null}
        </div>
      </div>

      {statusNote ? (
        <p className={statusError ? styles.error : styles.text}>{statusNote}</p>
      ) : null}

      {leading}

      <div className={styles.toggleWrap}>
        <button
          type="button"
          className={styles.summaryToggle}
          onClick={() => setSummaryOpen((open) => !open)}
        >
          {summaryOpen ? <FiChevronUp /> : <FiChevronDown />}
          {summaryOpen ? "Less info" : "More info"}
        </button>
      </div>

      {summaryOpen && (
        <>
          {more}

          {!hideSummary && (briefLines.length || display.bias || generatedAt) && (
            <div className={styles.boardPanel}>
              {brief}
            </div>
          )}

          {error && !loading && !hideSummary && (
            <p className={styles.error}>Couldn't load the summary. Try again.</p>
          )}

          {!hideSummary && (canCopy || exportLinks?.pdf_report) && (
            <div className={styles.exports}>
              {EXPORT_ITEMS.map((item) => {
                if (exportLinks?.[item.key]) {
                  return (
                    <a
                      key={item.key}
                      href={exportLinks[item.key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.exportLink}
                    >
                      <span className={styles.exportIcon}>{item.icon}</span>
                      {item.label}
                    </a>
                  );
                }
                if (!item.local || !canCopy) return null;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={styles.exportLink}
                    onClick={() => downloadLocal(item)}
                  >
                    <span className={styles.exportIcon}>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
              {canCopy && (
                <button type="button" className={styles.exportLink} onClick={copyBoard}>
                  <span className={styles.exportIcon}>
                    <FaRegCopy />
                  </span>
                  {copied ? "Copied" : "Copy"}
                </button>
              )}
            </div>
          )}

          {!hideSummary && outdated && (
            <p className={styles.note}>
              Signals were updated. This AI report is from the previous board.
            </p>
          )}

          {onRequest && (
            <Button
              className={styles.action}
              onClick={onRequest}
              loading={loading}
              disabled={disabled || loading}
              icon={!loading && Boolean(summaryText) ? <FiRefreshCcw /> : null}
            >
              {resolvedActionLabel}
            </Button>
          )}

          {!hideSummary && exportLinks?.pdf_report && (
            <p className={styles.note}>
              Server download links expire after 1 hour. Refresh the report to mint new ones.
            </p>
          )}

          {!hideSummary && hasVisual && (
            <div className={styles.visualBoard}>
              <AssistantVisualBoard visualSummary={visualSummary} />
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default ReportBoard;
